import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { SEO } from '@/components';
import { FiArrowLeft, FiMail, FiMessageCircle, FiHelpCircle, FiBook, FiAlertCircle } from 'react-icons/fi';
import { track } from '@/lib/analytics';

// Simple Header for legal/support pages
function LegalHeader() {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link to='/' search={{ hero: undefined }} className='text-white text-2xl font-semibold tracking-tight'>
            Harbor
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium'
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </div>
      </nav>
    </header>
  );
}

// Contact Card Component
function ContactCard({
  icon: Icon,
  title,
  description,
  action,
  actionLabel,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  action: string;
  actionLabel: string;
}) {
  return (
    <div className='bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100'>
      <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-5'>
        <Icon className='w-7 h-7 text-primary' />
      </div>
      <h3 className='text-xl font-bold text-gray-900 mb-3'>{title}</h3>
      <p className='text-gray-600 leading-relaxed mb-5'>{description}</p>
      <a
        href={action}
        className='inline-flex items-center gap-2 text-primary font-semibold hover:text-primary-hover transition-colors'
      >
        {actionLabel}
        <span aria-hidden='true'>→</span>
      </a>
    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <div className='border-b border-gray-200 pb-6'>
      <h3 className='text-lg font-semibold text-gray-900 mb-2'>{question}</h3>
      <p className='text-gray-600 leading-relaxed'>{answer}</p>
    </div>
  );
}

const Support = () => {
  useEffect(() => {
    track('Support Screen');
  }, []);

  const faqs = [
    {
      question: 'How do I invite my co-parent to Harbor?',
      answer:
        'Once you create a household, you can invite your co-parent by going to Settings > Household > Invite Member. Enter their phone number or email, and they will receive an invitation to join your household.',
    },
    {
      question: 'How does expense splitting work?',
      answer:
        'When you log an expense, you can specify how it should be split between co-parents (50/50, custom percentage, or assign to one parent). Harbor automatically calculates running balances so you always know who owes whom.',
    },
    {
      question: 'Is my data private and secure?',
      answer:
        'Yes, your data is encrypted both in transit and at rest. Only members of your household can see your shared information. We never sell your data to third parties.',
    },
    {
      question: 'Can I export my expense history?',
      answer:
        'Yes, Pro users can export their expense history as a CSV or PDF report. This is useful for record-keeping, tax purposes, or legal documentation.',
    },
    {
      question: 'How do I record a payment made to my co-parent?',
      answer:
        'Go to the Payments tab and tap "Record Payment". Enter the amount, date, and optionally add a note. Both co-parents will see this payment reflected in the running balance.',
    },
    {
      question: 'What happens if my co-parent disputes an expense?',
      answer:
        'If there is a disagreement about an expense, you can discuss it through the in-app messaging feature. All expenses are logged with timestamps and can include receipt attachments for documentation.',
    },
    {
      question: 'How do I delete my account?',
      answer:
        'You can request account deletion by contacting us at team@harborcoparent.com. We will process your request within 30 days and permanently delete your personal data as per our privacy policy.',
    },
    {
      question: 'Does Harbor offer fee waivers?',
      answer:
        'Yes. Parents who qualify for a court fee waiver, means-tested public benefits, or who are domestic violence survivors can email us for 12 months of access. See harborcoparent.com/fee-waivers for the proof we need.',
    },
  ];

  return (
    <div className='bg-white min-h-screen'>
      <SEO
        title='Help Center & FAQ | Harbor Co-Parenting App'
        description='Get help with Harbor. Find answers to common questions about expense tracking, co-parent invitations, data privacy, payment recording, and account management.'
        path='/support'
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />
      <LegalHeader />
      <div className='pt-24'>
        {/* Hero Section */}
        <div className='bg-primary/5 py-16'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
            <h1 className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'>
              How Can We Help?
            </h1>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              We're here to support you on your co-parenting journey. Find answers to common
              questions or reach out to our team directly.
            </p>
          </div>
        </div>

        {/* Contact Options */}
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16'>
          <h2 className='text-2xl font-bold text-gray-900 mb-8 text-center'>Get in Touch</h2>
          <div className='grid md:grid-cols-2 gap-6'>
            <ContactCard
              icon={FiMail}
              title='Email Support'
              description='Send us an email and we will get back to you within 24-48 hours.'
              action='mailto:team@harborcoparent.com'
              actionLabel='team@harborcoparent.com'
            />
            <ContactCard
              icon={FiMessageCircle}
              title='In-App Feedback'
              description='Use the feedback button in the app to report bugs or suggest features.'
              action='#'
              actionLabel='Open the Harbor App'
            />
          </div>
        </div>

        {/* FAQ Section */}
        <div className='bg-gray-50 py-16'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center mb-12'>
              <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4'>
                <FiHelpCircle className='w-7 h-7 text-primary' />
              </div>
              <h2 className='text-2xl font-bold text-gray-900 mb-2'>Frequently Asked Questions</h2>
              <p className='text-gray-600'>Quick answers to common questions about Harbor.</p>
            </div>

            <div className='space-y-6'>
              {faqs.map((faq) => (
                <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16'>
          <h2 className='text-2xl font-bold text-gray-900 mb-8 text-center'>Additional Resources</h2>
          <div className='grid md:grid-cols-3 gap-6'>
            <ContactCard
              icon={FiBook}
              title='Privacy Policy'
              description='Learn how we collect, use, and protect your personal information.'
              action='/privacy-policy'
              actionLabel='Read Privacy Policy'
            />
            <ContactCard
              icon={FiAlertCircle}
              title='Terms of Service'
              description='Review the terms and conditions for using Harbor.'
              action='/terms-of-service'
              actionLabel='Read Terms of Service'
            />
            <ContactCard
              icon={FiHelpCircle}
              title='Fee Waivers'
              description='If you cannot afford Harbor, email us with the same kind of proof other co-parenting apps ask for.'
              action='/fee-waivers'
              actionLabel='Read fee waiver instructions'
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className='bg-primary py-16'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
            <h2 className='text-3xl font-bold text-white mb-4'>Still Have Questions?</h2>
            <p className='text-xl text-white/80 mb-8'>
              Our support team is ready to help you with any questions or concerns.
            </p>
            <a
              href='mailto:team@harborcoparent.com'
              className='inline-block bg-white hover:bg-gray-100 text-primary font-semibold px-8 py-4 rounded-lg transition-all hover:shadow-xl text-lg'
            >
              Contact Support
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className='py-8 border-t border-gray-200'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
            <p className='text-center text-gray-400 text-sm'>
              &copy; {new Date().getFullYear()} Harbor. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/support')({
  component: Support,
});

